/**
 * Leave encashment and year-end processing — enums and schemas (ADR 0031).
 *
 * WHAT IS DECIDED HERE and every implementation must agree with: what a leave type's year-end can
 * do with the days an employee has left, how an encashed day is priced (basis and divisor), when
 * it is paid, and the lifecycle of the two records this feature adds — a year-end RUN (HR's
 * draft-then-apply batch) and an ENCASHMENT REQUEST (an employee asking to be paid for days).
 *
 * NOT DECIDED HERE: the money. The pure calculation lives in `@hrms/payroll`; nothing in this file
 * multiplies. Every amount is a decimal STRING (CLAUDE.md invariant 2) with its currency beside it,
 * every day count is a string too (`leaveDaysSchema` for input, `signedDaysSchema` for balance
 * figures that may go negative). The Section 10(10AA) exemption is NOT modelled: until payroll
 * compliance supplies the cap and the rules, an encashment payment is wholly taxable.
 *
 * NO POLICY ROW = NOT ENCASHABLE. A leave type with no `LeaveEncashmentPolicy` behaves exactly as
 * it did before this feature existed. Policy rows are effective-dated (invariant 5): a new
 * `validFrom` closes the open row, it never edits it.
 *
 * RN-SAFE: zod only, no Node built-ins.
 */

import { z } from 'zod';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  instantSchema,
  leaveDaysSchema,
  orgIdSchema,
  uuidSchema,
} from './schemas/common.js';

/** A day count that may be fractional, zero or negative — a balance figure, never an input amount.
 * Same shape as `signedDaysSchema` in `leave-balance-admin.contract.ts`, redeclared here so this
 * file depends on no contract (contracts import it, never the reverse). */
const dayFigureSchema = z.string().regex(/^-?\d+(\.\d+)?$/, 'Must be a decimal day count');

// ---------------------------------------------------------------------------
// Policy vocabulary
// ---------------------------------------------------------------------------

/**
 * What a year-end does with the days left when a leave year closes.
 *   - `lapse`                — all remaining days are written off.
 *   - `carry_all`            — all remaining days carry into the new year (subject to the leave
 *                              type's own max-carry rule, as today).
 *   - `encash_all`           — all remaining days are paid out.
 *   - `encash_x_carry_rest`  — up to `thresholdDays` are paid out, the rest carry forward.
 *   - `carry_x_encash_rest`  — up to `thresholdDays` carry forward, the rest are paid out.
 */
export const LEAVE_YEAR_END_ACTIONS = [
  'lapse',
  'carry_all',
  'encash_all',
  'encash_x_carry_rest',
  'carry_x_encash_rest',
] as const;
export const leaveYearEndActionSchema = z.enum(LEAVE_YEAR_END_ACTIONS);
export type LeaveYearEndAction = z.infer<typeof leaveYearEndActionSchema>;

/** The two actions that need a `thresholdDays` figure. */
export const LEAVE_YEAR_END_MIXED_ACTIONS = ['encash_x_carry_rest', 'carry_x_encash_rest'] as const;

/** An action that can put days up for payment — such a leave type must be `encashable`, and the
 * scheduled year-end job leaves it alone so it waits for HR's year-end run. */
export function leaveYearEndActionEncashes(action: LeaveYearEndAction): boolean {
  return action === 'encash_all' || action === 'encash_x_carry_rest' || action === 'carry_x_encash_rest';
}

/** Which salary figure a day is priced from. `component` names a pay component by code. */
export const LEAVE_ENCASHMENT_PAY_BASES = ['basic', 'basic_da', 'gross', 'component'] as const;
export const leaveEncashmentPayBasisSchema = z.enum(LEAVE_ENCASHMENT_PAY_BASES);
export type LeaveEncashmentPayBasis = z.infer<typeof leaveEncashmentPayBasisSchema>;

/** What the monthly basis is divided by to get a day's pay: a fixed 26 or 30, or the actual days
 * in the month the encashment is paid. */
export const LEAVE_ENCASHMENT_DIVISORS = ['26', '30', 'actual'] as const;
export const leaveEncashmentDivisorSchema = z.enum(LEAVE_ENCASHMENT_DIVISORS);
export type LeaveEncashmentDivisor = z.infer<typeof leaveEncashmentDivisorSchema>;

/** `next_payroll`: paid in the next payroll run after approval. `fnf_only`: never paid mid-service,
 * only at full & final settlement. */
export const LEAVE_ENCASHMENT_PAID_THROUGH = ['next_payroll', 'fnf_only'] as const;
export const leaveEncashmentPaidThroughSchema = z.enum(LEAVE_ENCASHMENT_PAID_THROUGH);
export type LeaveEncashmentPaidThrough = z.infer<typeof leaveEncashmentPaidThroughSchema>;

/** The defaults the design fixed: pay basis Basic, per-day divisor 30, encashment off. */
export const LEAVE_ENCASHMENT_DEFAULTS = {
  payBasis: 'basic',
  divisor: '30',
  paidThrough: 'next_payroll',
  yearEndAction: 'carry_all',
} as const satisfies {
  payBasis: LeaveEncashmentPayBasis;
  divisor: LeaveEncashmentDivisor;
  paidThrough: LeaveEncashmentPaidThrough;
  yearEndAction: LeaveYearEndAction;
};

// ---------------------------------------------------------------------------
// Encashment policy (effective-dated, one open row per leave type)
// ---------------------------------------------------------------------------

export const leaveEncashmentPolicySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  leaveTypeId: uuidSchema,
  /** False keeps the row (and its history) but turns encashment off for the leave type. */
  encashable: z.boolean(),
  payBasis: leaveEncashmentPayBasisSchema,
  /** The pay component's code when `payBasis` is `component`; null otherwise. */
  payComponentCode: z.string().nullable(),
  divisor: leaveEncashmentDivisorSchema,
  yearEndAction: leaveYearEndActionSchema,
  /** X in the two mixed actions; null for the others. */
  thresholdDays: leaveDaysSchema.nullable(),
  /** A request may not leave fewer than this many days in the balance. Null = no floor. */
  minRemainingDays: leaveDaysSchema.nullable(),
  /** Most days one person may encash in one leave year, across requests. Null = no cap. */
  maxDaysPerYear: leaveDaysSchema.nullable(),
  paidThrough: leaveEncashmentPaidThroughSchema,
  validFrom: dateOnlySchema,
  /** Null on the open row. */
  validTo: dateOnlySchema.nullable(),
  createdAt: instantSchema,
});
export type LeaveEncashmentPolicy = z.infer<typeof leaveEncashmentPolicySchema>;

/** One leave type with its policy as of the query's date; `policy` is null when none applies —
 * which means not encashable and unchanged year-end behaviour. */
export const leaveEncashmentPolicyListItemSchema = z.object({
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  policy: leaveEncashmentPolicySchema.nullable(),
});
export type LeaveEncashmentPolicyListItem = z.infer<typeof leaveEncashmentPolicyListItemSchema>;

export const listLeaveEncashmentPoliciesQuerySchema = z.object({
  /** The date to resolve policies on, in the org's own terms; absent = today. */
  asOf: dateOnlySchema.optional(),
});

export const setLeaveEncashmentPolicyBodySchema = z
  .object({
    encashable: z.boolean(),
    payBasis: leaveEncashmentPayBasisSchema.default(LEAVE_ENCASHMENT_DEFAULTS.payBasis),
    payComponentCode: z.string().trim().min(1).max(64).nullable().default(null),
    divisor: leaveEncashmentDivisorSchema.default(LEAVE_ENCASHMENT_DEFAULTS.divisor),
    yearEndAction: leaveYearEndActionSchema.default(LEAVE_ENCASHMENT_DEFAULTS.yearEndAction),
    thresholdDays: leaveDaysSchema.nullable().default(null),
    minRemainingDays: leaveDaysSchema.nullable().default(null),
    maxDaysPerYear: leaveDaysSchema.nullable().default(null),
    paidThrough: leaveEncashmentPaidThroughSchema.default(LEAVE_ENCASHMENT_DEFAULTS.paidThrough),
    /** When this version takes effect. The open row closes the day before; a date at or before the
     * open row's own `validFrom` is refused — history is never rewritten. */
    validFrom: dateOnlySchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.payBasis === 'component' && !value.payComponentCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payComponentCode'],
        message: 'A component pay basis needs the component code',
      });
    }
    if (value.payBasis !== 'component' && value.payComponentCode !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payComponentCode'],
        message: 'Only a component pay basis takes a component code',
      });
    }
    const mixed = (LEAVE_YEAR_END_MIXED_ACTIONS as readonly string[]).includes(value.yearEndAction);
    if (mixed && value.thresholdDays === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['thresholdDays'],
        message: 'This year-end action needs a threshold in days',
      });
    }
    if (!mixed && value.thresholdDays !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['thresholdDays'],
        message: 'Only the split year-end actions take a threshold',
      });
    }
    if (leaveYearEndActionEncashes(value.yearEndAction) && !value.encashable) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['encashable'],
        message: 'A year-end action that pays out days needs encashment switched on',
      });
    }
  });
export type SetLeaveEncashmentPolicyBody = z.infer<typeof setLeaveEncashmentPolicyBodySchema>;

// ---------------------------------------------------------------------------
// Year-end runs (HR: draft preview, then apply)
// ---------------------------------------------------------------------------

/** `draft` writes nothing to the ledger. `applied` is final — a correction is a balance
 * adjustment, never a re-open. `discarded` is a draft HR abandoned. */
export const LEAVE_YEAR_END_RUN_STATUSES = ['draft', 'applied', 'discarded'] as const;
export const leaveYearEndRunStatusSchema = z.enum(LEAVE_YEAR_END_RUN_STATUSES);
export type LeaveYearEndRunStatus = z.infer<typeof leaveYearEndRunStatusSchema>;

export const createLeaveYearEndRunBodySchema = z
  .object({
    legalEntityId: uuidSchema,
    /** The leave year being CLOSED, as the entity's fiscal/calendar basis numbers it. */
    leaveYear: z.number().int().min(2000).max(2100),
    /** Absent = every leave type with a year-end to process. */
    leaveTypeIds: z.array(uuidSchema).min(1).max(100).optional(),
  })
  .strict();

/** One person's outcome for one leave type. `carriedDays + encashedDays + lapsedDays` always
 * equals `balanceDays`. */
export const leaveYearEndLineSchema = z.object({
  id: uuidSchema,
  runId: uuidSchema,
  personId: uuidSchema,
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().nullable(),
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  /** The policy action that produced the split. */
  action: leaveYearEndActionSchema,
  balanceDays: dayFigureSchema,
  carriedDays: dayFigureSchema,
  encashedDays: dayFigureSchema,
  lapsedDays: dayFigureSchema,
  /** Null when nothing is encashed, or the person's pay basis could not be resolved (see
   * `warnings`). An estimate: the payroll run computes the figure that is actually paid. */
  estimatedAmount: decimalStringSchema.nullable(),
  currency: currencySchema.nullable(),
  /** Why a line needs a human's eye, e.g. no salary on record for the pay basis. */
  warnings: z.array(z.string()),
});
export type LeaveYearEndLine = z.infer<typeof leaveYearEndLineSchema>;

export const leaveYearEndRunSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  leaveYear: z.number().int(),
  leaveTypeIds: z.array(uuidSchema),
  status: leaveYearEndRunStatusSchema,
  lineCount: z.number().int().nonnegative(),
  totalCarriedDays: dayFigureSchema,
  totalEncashedDays: dayFigureSchema,
  totalLapsedDays: dayFigureSchema,
  /** Sum of the lines' estimates, in the entity's currency; null when nothing is encashed. */
  estimatedEncashmentAmount: decimalStringSchema.nullable(),
  currency: currencySchema.nullable(),
  /** Visible on every run until payroll compliance supplies the Section 10(10AA) rules. */
  taxNote: z.string(),
  createdByName: z.string().nullable(),
  createdAt: instantSchema,
  appliedAt: instantSchema.nullable(),
  appliedByName: z.string().nullable(),
  version: z.number().int().positive(),
});
export type LeaveYearEndRun = z.infer<typeof leaveYearEndRunSchema>;

export const leaveYearEndLinesQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  leaveTypeId: uuidSchema.optional(),
  /** Matches name or employee code, case-insensitively. */
  search: z.string().trim().max(100).optional(),
  /** Only lines that carry a warning. */
  warningsOnly: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});

export const listLeaveYearEndRunsQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  legalEntityId: uuidSchema.optional(),
  leaveYear: z.coerce.number().int().min(2000).max(2100).optional(),
  status: leaveYearEndRunStatusSchema.optional(),
});

export const applyLeaveYearEndRunBodySchema = z
  .object({ expectedVersion: z.number().int().positive() })
  .strict();

export const discardLeaveYearEndRunBodySchema = z
  .object({ expectedVersion: z.number().int().positive() })
  .strict();

// ---------------------------------------------------------------------------
// Encashment requests (employee, approved through @hrms/workflow)
// ---------------------------------------------------------------------------

/**
 * `pending` (submitted, ledger already debited) → `approved` | `rejected` | `withdrawn`.
 * `approved` → `reserved` (picked into a draft payroll run) → `paid` (that run committed);
 * releasing the reservation returns it to `approved`. `rejected` and `withdrawn` write an
 * `encashment_reversal` ledger entry; `reversed` is a paid request undone by a payroll reversal.
 */
export const LEAVE_ENCASHMENT_REQUEST_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'withdrawn',
  'reserved',
  'paid',
  'reversed',
] as const;
export const leaveEncashmentRequestStatusSchema = z.enum(LEAVE_ENCASHMENT_REQUEST_STATUSES);
export type LeaveEncashmentRequestStatus = z.infer<typeof leaveEncashmentRequestStatusSchema>;

export const leaveEncashmentRequestSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  employmentId: uuidSchema,
  employeeName: z.string(),
  employeeCode: z.string().nullable(),
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  leaveYear: z.number().int(),
  days: leaveDaysSchema,
  status: leaveEncashmentRequestStatusSchema,
  paidThrough: leaveEncashmentPaidThroughSchema,
  /** Estimate at filing; null when the pay basis could not be resolved. Not what is paid. */
  estimatedAmount: decimalStringSchema.nullable(),
  /** Set once a payroll run carries the request; the figure on the payslip. */
  paidAmount: decimalStringSchema.nullable(),
  currency: currencySchema.nullable(),
  payrollRunId: uuidSchema.nullable(),
  reason: z.string().nullable(),
  decidedAt: instantSchema.nullable(),
  decidedByName: z.string().nullable(),
  decisionNote: z.string().nullable(),
  workflowInstanceId: uuidSchema.nullable(),
  createdAt: instantSchema,
  version: z.number().int().positive(),
});
export type LeaveEncashmentRequest = z.infer<typeof leaveEncashmentRequestSchema>;

export const createLeaveEncashmentRequestBodySchema = z
  .object({
    leaveTypeId: uuidSchema,
    days: leaveDaysSchema,
    reason: z.string().trim().max(500).optional(),
  })
  .strict();

export const listLeaveEncashmentRequestsQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  status: leaveEncashmentRequestStatusSchema.optional(),
  leaveTypeId: uuidSchema.optional(),
  /** `mine`: the caller's own. `team`: the caller's reporting tree. `all`: needs
   * `leave.year_end:read`. */
  scope: z.enum(['mine', 'team', 'all']).default('mine'),
});

export const decideLeaveEncashmentRequestBodySchema = z
  .object({ comment: z.string().max(500).optional() })
  .strict();

export const withdrawLeaveEncashmentRequestBodySchema = z
  .object({ reason: z.string().trim().max(500).optional() })
  .strict();
